import type { RfqProject, RfqPackage, TimberSupplierInvite } from '../types';

/** All supplier org IDs assigned to an RFQ project (legacy single field or explicit array). */
export function rfqProjectSupplierOrgIds(
  project: Partial<Pick<RfqProject, 'supplierOrgId' | 'supplierOrgIds'>>,
): string[] {
  // An explicit array (including empty) means suppliers were managed via supplierOrgIds.
  if (Array.isArray(project.supplierOrgIds)) {
    return Array.from(new Set(project.supplierOrgIds.filter((x): x is string => Boolean(x))));
  }
  return project.supplierOrgId ? [project.supplierOrgId] : [];
}

/** Unique supplier org ids from published packages (stable order). */
export function supplierOrgIdsFromPackages(packages: Pick<RfqPackage, 'supplierOrgId'>[]): string[] {
  const ids: string[] = [];
  for (const pkg of packages) {
    const id = (pkg.supplierOrgId || '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Suppliers shown for a selected RFQ release: current publish roster plus anyone
 * published on that release (so prior-version quotes stay visible when the roster shrinks).
 */
export function supplierOrgIdsForReleaseSelection(
  packagesForRelease: Pick<RfqPackage, 'supplierOrgId'>[],
  projectSupplierOrgIds: string[],
): string[] {
  const merged = [...projectSupplierOrgIds];
  for (const id of supplierOrgIdsFromPackages(packagesForRelease)) {
    if (!merged.includes(id)) merged.push(id);
  }
  return merged;
}

/** True when an RFQ project still references a vendor org (assignments or timber invites). */
export function rfqProjectReferencesSupplierOrg(
  project: Partial<Pick<RfqProject, 'supplierOrgId' | 'supplierOrgIds' | 'timberSupplierInvites'>>,
  orgId: string,
): boolean {
  if (rfqProjectSupplierOrgIds(project).includes(orgId)) return true;
  return (project.timberSupplierInvites ?? []).some((invite) => invite.supplierOrgId === orgId);
}

export type RfqSupplierModalSaveInput = {
  supplierOrgIds: string[];
  supplierOrgId: string;
  supplierOrgName?: string;
  timberSupplierInvites: TimberSupplierInvite[];
  approvedSupplierOrgId?: string;
};

/** Strip Firestore FieldValue sentinels (e.g. deleteField) from cached snapshot data. */
export function sanitizeFirestoreFieldValues<T extends Record<string, unknown>>(data: T): T {
  const out = { ...data };
  for (const [key, value] of Object.entries(out)) {
    if (
      value != null
      && typeof value === 'object'
      && '_methodName' in value
      && typeof (value as { _methodName?: unknown })._methodName === 'string'
    ) {
      delete out[key];
    }
  }
  return out;
}

/** Map modal save payload to Firestore updates, including clearing legacy primary supplier fields. */
export function buildRfqSupplierSaveUpdates(
  updates: RfqSupplierModalSaveInput,
): Record<string, unknown> {
  if (!updates.supplierOrgIds.length) {
    return {
      supplierOrgIds: [],
      timberSupplierInvites: [],
      // Use null (not deleteField) — FieldValue sentinels can briefly appear in local
      // snapshot cache and crash React if rendered as text.
      supplierOrgId: null,
      supplierOrgName: null,
      approvedSupplierOrgId: null,
      approvedSubmissionIds: null,
      approvedSubmissionIdsByRelease: null,
      approvedSupplierOrgIdByRelease: null,
      approvedSupplierOrgIdsByRelease: null,
    };
  }
  const payload: Record<string, unknown> = {
    supplierOrgIds: updates.supplierOrgIds,
    supplierOrgId: updates.supplierOrgId,
    timberSupplierInvites: updates.timberSupplierInvites,
  };
  if (updates.supplierOrgName !== undefined) {
    payload.supplierOrgName = updates.supplierOrgName;
  }
  payload.approvedSupplierOrgId = updates.approvedSupplierOrgId ?? null;
  return payload;
}
